import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { StorageService } from '../../services/storage-service';
import { AppUrl } from '../../appUrl';
import { ThaidatePipe } from '../../pipe/thaidate-pipe';
import { TranslateModule } from '../../shareds/translate/translate.module';

@Component({
  selector: 'app-booking-info',
  standalone: true,
  imports: [CommonModule, ThaidatePipe, TranslateModule],
  templateUrl: './booking-info.component.html',
  styleUrls: ['./booking-info.component.css']
})
export class BookingInfoComponent implements OnInit {
  private router = inject(Router);
  private ac = inject(ActivatedRoute);
  private st = inject(StorageService);
  private http = inject(HttpClient);

  bookingData = signal<any>(null);
  customerInfo = signal<any>(null);
  confirmLoading = signal(false);
  confirmError = signal('');

  ngOnInit() {
    const bd = this.st.getSession('bookingData');
    const ci = this.st.getSession('customerInfo');
    if (bd) this.bookingData.set(JSON.parse(bd));
    if (ci) this.customerInfo.set(JSON.parse(ci));
  }

  // กระจายผู้เข้าพักตาม capacity ของแต่ละห้อง รวมต้องครบทุกคน
  private distributeGuests(rooms: any[], totalAdults: number, totalChildren: number) {
    const totalGuests = totalAdults + totalChildren;
    if (totalGuests === 0) {
      return rooms.map((r: any) => ({
        roomTypeId: r.roomTypeId, adults: 0, children: 0, quantity: r.qty, pricingModel: r.planName,
      }));
    }

    // capacity ต่อ room type (ถ้าไม่มี maxCapacity ให้รับได้ทั้งหมด)
    const caps = rooms.map((r: any) => r.maxCapacity > 0 ? r.qty * r.maxCapacity : totalGuests);
    const totalCap = caps.reduce((a: number, b: number) => a + b, 0);

    let remAdults = totalAdults;
    let remChildren = totalChildren;

    return rooms.map((r: any, i: number) => {
      const isLast = i === rooms.length - 1;
      let guests: number;
      let adults: number;
      let children: number;

      if (isLast) {
        adults = remAdults;
        children = remChildren;
      } else {
        const ratio = caps[i] / totalCap;
        guests = Math.min(Math.round(totalGuests * ratio), caps[i]);
        // กระจาย adults/children ตามสัดส่วน
        adults = Math.min(Math.round(totalAdults * ratio), guests, remAdults);
        children = Math.min(guests - adults, remChildren);
        adults = guests - children; // ปรับ adults ให้ guests ครบ
        remAdults -= adults;
        remChildren -= children;
      }

      return { roomTypeId: r.roomTypeId, adults, children, quantity: r.qty, pricingModel: r.planName };
    });
  }

  toBack() {
    this.router.navigate(['/', AppUrl.customer_info, this.ac.snapshot.params['app-id']]);
  }

  confirmBooking() {
    const bd = this.bookingData();
    const ci = this.customerInfo();
    if (!bd || !ci) return;

    const phoneCode = ci.phoneCode ?? '';

    // guestName = ผู้เข้าพักหลัก ถ้าติ๊ก notMainGuest ไว้
    const guestName = ci.notMainGuest && ci.mainGuestName ? ci.mainGuestName : ci.name;

    const body = {
      channel: 1,
      branchId: bd.branchId,
      checkIn: bd.checkIn,
      checkOut: bd.checkOut,
      guestName: guestName,
      specialRequest: '',
      contact: {
        name: ci.name,
        phoneCode: phoneCode,
        phone: ci.phone,
        email: ci.email,
      },
      rooms: this.distributeGuests(bd.selectedRooms, bd.totalAdults, bd.totalChildren),
    };

    this.confirmLoading.set(true);
    this.confirmError.set('');

    this.http.post<any>(`/api/Public/ReserveHotel/Reservation?BranchId=${bd.branchId}`, body).subscribe({
      next: (res) => {
        this.confirmLoading.set(false);
        // เก็บข้อมูลการจองไว้แสดงในหน้า success
        this.st.setSession('lastBookingData', JSON.stringify(bd));
        this.st.setSession('lastBookingResult', JSON.stringify(res?.data ?? res ?? {}));
        this.st.setSession('lastContactEmail', ci.email ?? '');
        this.router.navigate(['/', AppUrl.payment, this.ac.snapshot.params['app-id']]);
      },
      error: (err) => {
        this.confirmLoading.set(false);
        this.confirmError.set(err?.error?.message ?? 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'); // แปลใน template (module 'api')
      },
    });
  }
}
